/*!
VueLayers
Vue 2 components to work with OpenLayers

@package vuelayers
@author Vladimir Vershinin <ghettovoice@gmail.com>
@version 0.9.1
@license MIT
@copyright (c) 2017-2018, Vladimir Vershinin <ghettovoice@gmail.com>
*/
import _extends from 'babel-runtime/helpers/extends';
import circleStyle from './circle-style';
import clusterSource from './cluster-source';
import feature from './feature';
import fillStyle from './fill-style';
import geoloc from './geoloc';
import iconStyle from './icon-style';
import imageLayer from './image-layer';
import imageStaticSource from './image-static-source';
import lineStringGeom from './line-string-geom';
import map from './map';
import mapboxSource from './mapbox-source';
import multiLineStringGeom from './multi-line-string-geom';
import multiPointGeom from './multi-point-geom';
import multiPolygonGeom from './multi-polygon-geom';
import osmSource from './osm-source';
import overlay from './overlay';
import pointGeom from './point-geom';
import polygonGeom from './polygon-geom';
import regShapeStyle from './reg-shape-style';
import selectInteraction from './select-interaction';
import sputnikSource from './sputnik-source';
import strokeStyle from './stroke-style';
import styleBox from './style-box';
import styleFunc from './style-func';
import textStyle from './text-style';
import tileLayer from './tile-layer';
import vectorLayer from './vector-layer';
import vectorSource from './vector-source';
import wmsSource from './wms-source';
import wmtsSource from './wmts-source';
import xyzSource from './xyz-source';
import { VL_OPTIONS, log, projHelper } from './core';
import * as core from './core';
import _Object$keys from 'babel-runtime/core-js/object/keys';



var components = Object.freeze({
	CircleStyle: circleStyle,
	ClusterSource: clusterSource,
	Feature: feature,
	FillStyle: fillStyle,
	Geoloc: geoloc,
	IconStyle: iconStyle,
	ImageLayer: imageLayer,
	ImageStaticSource: imageStaticSource,
	LineStringGeom: lineStringGeom,
	Map: map,
	MapboxSource: mapboxSource,
	MultiLineStringGeom: multiLineStringGeom,
	MultiPointGeom: multiPointGeom,
	MultiPolygonGeom: multiPolygonGeom,
	OsmSource: osmSource,
	Overlay: overlay,
	PointGeom: pointGeom,
	PolygonGeom: polygonGeom,
	RegShapeStyle: regShapeStyle,
	SelectInteraction: selectInteraction,
	SputnikSource: sputnikSource,
	StrokeStyle: strokeStyle,
	StyleBox: styleBox,
	StyleFunc: styleFunc,
	TextStyle: textStyle,
	TileLayer: tileLayer,
	VectorLayer: vectorLayer,
	VectorSource: vectorSource,
	WmsSource: wmsSource,
	WmtsSource: wmtsSource,
	XyzSource: xyzSource
});

/**
 * Registers all VueLayers components.
 * @param {Vue} Vue
 * @param {VueLayersOptions} [options]
 */
function install$1(Vue) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  if (install$1.installed) return;
  install$1.installed = true;

  if (options.bindToProj && !projHelper.get(options.bindToProj)) {
    log.warn('Projection "' + options.bindToProj + '" isn\'t added to the list of known projections. ' + 'It should be added before VueLayers install with OpenLayers or VueLayers API.');
  }
  // extend Vue with VueLayers global methods and options
  Vue[VL_OPTIONS] = Vue.prototype[VL_OPTIONS] = options;

  // install all components
  _Object$keys(components).forEach(function (key) {
    if (typeof components[key].install === 'function') {
      Vue.use(components[key]);
    }
  });
}

/**
 * @typedef {Object} VueLayersOptions
 * @property {string} [bindToProj] Bind all props to the provided projection.
 */

/**
 * Entry file for non-ES environments.
 */
// import lib style
var VueLayers$1 = _extends({
  // meta & consts
  VERSION: '0.9.1',
  // install
  install: install$1,
  core: core
}, components);

/**
 * Entry file for ES environments.
 */
var install = VueLayers$1.install;
var core$1 = VueLayers$1.core;
var CircleStyle = VueLayers$1.CircleStyle;
var ClusterSource = VueLayers$1.ClusterSource;
var Feature = VueLayers$1.Feature;
var FillStyle = VueLayers$1.FillStyle;
var Geoloc = VueLayers$1.Geoloc;
var IconStyle = VueLayers$1.IconStyle;
var ImageLayer = VueLayers$1.ImageLayer;
var ImageStaticSource = VueLayers$1.ImageStaticSource;
var LineStringGeom = VueLayers$1.LineStringGeom;
var Map = VueLayers$1.Map;
var MapboxSource = VueLayers$1.MapboxSource;
var MultiLineStringGeom = VueLayers$1.MultiLineStringGeom;
var MultiPointGeom = VueLayers$1.MultiPointGeom;
var MultiPolygonGeom = VueLayers$1.MultiPolygonGeom;
var OsmSource = VueLayers$1.OsmSource;
var Overlay = VueLayers$1.Overlay;
var PointGeom = VueLayers$1.PointGeom;
var PolygonGeom = VueLayers$1.PolygonGeom;
var RegShapeStyle = VueLayers$1.RegShapeStyle;
var SelectInteraction = VueLayers$1.SelectInteraction;
var SputnikSource = VueLayers$1.SputnikSource;
var StrokeStyle = VueLayers$1.StrokeStyle;
var StyleBox = VueLayers$1.StyleBox;
var StyleContainer = VueLayers$1.StyleContainer;
var StyleFunc = VueLayers$1.StyleFunc;
var TextStyle = VueLayers$1.TextStyle;
var TileLayer = VueLayers$1.TileLayer;
var VectorLayer = VueLayers$1.VectorLayer;
var VectorSource = VueLayers$1.VectorSource;
var WmsSource = VueLayers$1.WmsSource;
var WmtsSource = VueLayers$1.WmtsSource;
var XyzSource = VueLayers$1.XyzSource;

export { install, core$1 as core, CircleStyle, ClusterSource, Feature, FillStyle, Geoloc, IconStyle, ImageLayer, ImageStaticSource, LineStringGeom, Map, MapboxSource, MultiLineStringGeom, MultiPointGeom, MultiPolygonGeom, OsmSource, Overlay, PointGeom, PolygonGeom, RegShapeStyle, SelectInteraction, SputnikSource, StrokeStyle, StyleBox, StyleContainer, StyleFunc, TextStyle, TileLayer, VectorLayer, VectorSource, WmsSource, WmtsSource, XyzSource };
export default VueLayers$1;

//# sourceMappingURL=index.es.js.map



//////////////////
// WEBPACK FOOTER
// ./node_modules/vuelayers/lib/index.es.js
// module id = null
// module chunks = 